/**
 * PA 대시보드 Store
 * - 클로저 패턴으로 상태 캡슐화
 * - 전사 성과 대시보드 데이터 관리
 * - 타입은 Store에서만 지정
 */
import { ref } from 'vue';
import { usePaDashboardApi } from './usePaDashboardApi';
import { usePaReportApi } from '~/composables/pa/report/usePaReportApi';
import { useCommonStore } from '~/stores/useCommonStore';
import { useCommonApi } from '~/composables/useCommonApi';
import { sortYearOptionsDesc } from '~/composables/pa/sortYearOptionsDesc';

// 타입 정의
interface SelectOption {
  label: string;
  value: string;
}

// 전사 성과 데이터
interface OverallPerformanceData {
  score: number | null; // 전사 성과 종합점수
  scoreChange: number | null; // 전월 대비 증감
}

// 전체 구성원 데이터
interface TotalMembersData {
  total: number | null; // 전체 구성원 수
  active: number | null; // 활성 구성원 수
}

// 그룹 데이터 (고성과/성과개선)
interface PerformanceGroupData {
  count: number | null; // 구성원 수
  changeCount: number | null; // 전월 대비 변경 수 (양수: 증가, 음수: 감소)
}

// 년도별 추이 데이터
interface YearlyTrendData {
  year: string; // 년도
  score: number; // 점수
}

// 월별 추이 데이터
interface MonthlyTrendData {
  month: string; // 월
  currentYearScore: number; // 현재 년도 점수
  previousYearScore: number; // 이전 년도 점수
}

// 조직 성과 순위 아이템
interface OrganizationRankItem {
  rank: number; // 순위
  name: string; // 조직명 (팀명/부서명/본부명)
  leader: string; // 리더 (팀장/부서장/본부장)
  score: number | null; // 성과수준 (점수)
  change: number | null; // 변화 (증감점수)
}

// 구성원 상세 데이터 (고성과자/성과개선 그룹용)
interface MemberDetailData {
  userId: string; // 아이디
  name: string; // 이름
  department: string; // 부서명
  position: string; // 직책
  score: number | null; // 점수
  scoreChange: number | null; // 전월 대비 증감 점수
  departmentColor?: string; // UI용 색상
  scoreColor?: string; // UI용 색상
}

// 구성원 성과 상세 데이터 (모달용 - 5개년도)
interface MemberPerformanceHistory {
  yearlyData: {
    year: string; // 년도
    score: number | null; // 점수 (null이면 데이터 없음)
  }[];
  currentYearPrediction: number | null; // 현재 년도 예측 점수
}

// F.O.C.U.S 인덱스 그룹 점수
interface IndexGroupData {
  fScore: number | null; // Function
  oScore: number | null; // Organization
  cScore: number | null; // Communication
  uScore: number | null; // Understanding
  sScore: number | null; // Solution
}

// 등급별 구성원 분포
interface PerformGradeData {
  sCnt: number | null; // S등급 인원
  aCnt: number | null; // A등급 인원
  bCnt: number | null; // B등급 인원
  cCnt: number | null; // C등급 인원
  dCnt: number | null; // D등급 인원
}

/**
 * 싱글톤 store 인스턴스
 */
let storeInstance: any = null;

/**
 * Export (싱글톤 패턴)
 */
export const usePaDashboardStore = () => {
  // 이미 인스턴스가 있으면 재사용
  if (storeInstance) {
    return storeInstance;
  }

  // API 가져오기
  const $api = usePaDashboardApi();
  const $reportApi = usePaReportApi();
  const $commonApi = useCommonApi();
  const commonStore = useCommonStore();

  // 검색 조건
  const selectedYear = ref<string>('2025');
  const selectedMonth = ref<string>('01');

  // 콤보박스 옵션
  const yearOptions = ref<SelectOption[]>([]);
  const monthOptions = ref<SelectOption[]>([]);

  // 목업 데이터 사용 여부 (개발 중에는 true, 배포 시에는 false)
  const useMockupData = ref<boolean>(false);

  // === 전사 성과 데이터 ===
  const overallPerformance = ref<OverallPerformanceData>({
    score: null,
    scoreChange: null,
  });

  // === 전체 구성원 데이터 ===
  const totalMembers = ref<TotalMembersData>({
    total: null,
    active: null,
  });

  // === 고성과 예측 그룹 데이터 ===
  const highPerformanceGroup = ref<PerformanceGroupData>({
    count: null,
    changeCount: null,
  });

  // === 성과개선 집중 그룹 데이터 ===
  const improvementGroup = ref<PerformanceGroupData>({
    count: null,
    changeCount: null,
  });

  // === F.O.C.U.S 인덱스 그룹 점수 ===
  const indexGroup = ref<IndexGroupData>({
    fScore: null,
    oScore: null,
    cScore: null,
    uScore: null,
    sScore: null,
  });

  // === 등급별 구성원 분포 ===
  const performGrade = ref<PerformGradeData>({
    sCnt: null,
    aCnt: null,
    bCnt: null,
    cCnt: null,
    dCnt: null,
  });

  // === 전사 성과 추이 데이터 (년도별) ===
  const yearlyTrendData = ref<YearlyTrendData[]>([
    { year: '2019', score: 75 },
    { year: '2020', score: 74 },
    { year: '2021', score: 78 },
    { year: '2022', score: 77 },
    { year: '2023', score: 80 },
    { year: '2024', score: 82 },
    { year: '2025', score: 88 },
  ]);

  // === 전사 성과 추이 데이터 (월별) ===
  const monthlyTrendData = ref<MonthlyTrendData[]>([
    { month: '1월', currentYearScore: 82.1, previousYearScore: 78.3 },
    { month: '2월', currentYearScore: 83.5, previousYearScore: 79.1 },
    { month: '3월', currentYearScore: 84.2, previousYearScore: 79.7 },
    { month: '4월', currentYearScore: 85.3, previousYearScore: 80.2 },
    { month: '5월', currentYearScore: 86.0, previousYearScore: 80.5 },
    { month: '6월', currentYearScore: 86.5, previousYearScore: 81.3 },
    { month: '7월', currentYearScore: 87.0, previousYearScore: 82.0 },
    { month: '8월', currentYearScore: 87.3, previousYearScore: 82.6 },
    { month: '9월', currentYearScore: 87.5, previousYearScore: 83.1 },
    { month: '10월', currentYearScore: 87.8, previousYearScore: 83.5 },
    { month: '11월', currentYearScore: 87.6, previousYearScore: 84.0 },
    { month: '12월', currentYearScore: 88.0, previousYearScore: 84.5 },
  ]);

  // === 조직 성과수준 데이터 ===
  // 팀별 성과 순위
  const teamRankings = ref<OrganizationRankItem[]>([]);

  // 부서별 성과 순위
  const departmentRankings = ref<OrganizationRankItem[]>([]);

  // 본부별 성과 순위
  const divisionRankings = ref<OrganizationRankItem[]>([]);

  // === 고성과자 예측 그룹 구성원 목록 ===
  const highPerformanceMembers = ref<MemberDetailData[]>([]);

  // === 성과개선 집중 그룹 구성원 목록 ===
  const improvementMembers = ref<MemberDetailData[]>([]);

  // === 구성원 성과 상세 데이터 (모달용) ===
  const memberPerformanceDetail = ref<MemberPerformanceHistory | null>(null);

  // 로딩 상태
  const isLoading = ref<boolean>(false);

  /**
   * 검색 옵션 조회 (년도/월)
   * @param updateYearOptions - 년도 옵션도 업데이트할지 여부 (기본: true)
   */
  const fetchSearchOptions = async (updateYearOptions = true) => {
    try {
      const response = await $reportApi.fetchSearchOptions(selectedYear.value);

      if (response) {
        // 년도 옵션 설정 (초기 로드 시에만)
        if (updateYearOptions && response.yearList) {
          yearOptions.value = sortYearOptionsDesc(
            response.yearList.map((item: any) => ({
              label: item.yearNm,
              value: item.year,
            })),
          );

          // selectedYear가 목록에 없으면 첫 번째 항목으로 설정
          const yearExists = yearOptions.value.some(opt => opt.value === selectedYear.value);
          if (!yearExists && yearOptions.value.length > 0) {
            selectedYear.value = yearOptions.value[0].value;
          }
        } else if (updateYearOptions) {
          yearOptions.value = [
            { label: '2025', value: '2025' },
            { label: '2024', value: '2024' },
            { label: '2023', value: '2023' },
          ];
        }

        console.log('📅 월 옵션 응답:', response.monthList);

        // 월 옵션 설정
        if (response.monthList && response.monthList.length > 0) {
          monthOptions.value = response.monthList.map((item: any) => {
            const monthValue = item.month || item.value;
            // "01" → "1월", "12" → "12월" 형식으로 변환
            const monthLabel = `${parseInt(monthValue, 10)}월`;
            return {
              label: monthLabel,
              value: monthValue,
            };
          });

          // selectedMonth가 새로운 목록에 없으면 첫 번째 항목으로 설정
          const monthExists = monthOptions.value.some(opt => opt.value === selectedMonth.value);
          if (!monthExists && monthOptions.value.length > 0) {
            selectedMonth.value = monthOptions.value[0].value;
            console.log('📅 월 자동 선택:', selectedMonth.value);
          }
        } else {
          // 해당 년도에 데이터가 없으면 빈 배열 유지
          monthOptions.value = [];
          console.log('📅 해당 년도에 사용 가능한 월 데이터 없음');
        }
      } else {
        // 기본값 설정
        if (updateYearOptions) {
          yearOptions.value = [
            { label: '2025', value: '2025' },
            { label: '2024', value: '2024' },
            { label: '2023', value: '2023' },
          ];
        }
        monthOptions.value = [];
      }
    } catch (error) {
      console.error('검색 옵션 조회 실패:', error);
      // 기본값 설정
      if (updateYearOptions) {
        yearOptions.value = [
          { label: '2025', value: '2025' },
          { label: '2024', value: '2024' },
          { label: '2023', value: '2023' },
        ];
      }
      monthOptions.value = [];
    }
  };

  /**
   * 월 옵션만 다시 조회 (년도 변경 시)
   * - 년도 변경 시에는 항상 첫 번째 월 선택
   */
  const fetchMonthOptions = async () => {
    console.log('📅 년도 변경으로 월 옵션 재조회:', selectedYear.value);
    await fetchSearchOptions(false); // 년도 옵션은 업데이트하지 않음

    // 년도 변경 시 항상 첫 번째 월 선택
    if (monthOptions.value.length > 0) {
      selectedMonth.value = monthOptions.value[0].value;
      console.log('📅 첫 번째 월 자동 선택:', selectedMonth.value);
    }
  };

  /**
   * 대시보드 데이터 조회 (검색 시 호출)
   */
  const fetchDashboardData = async (year: string, month: string) => {
    try {
      isLoading.value = true;

      // 목업 데이터 사용 모드일 때는 API 호출하지 않음
      if (useMockupData.value) {
        console.log('📊 Mockup 데이터 사용 중 (API 호출 생략)');
        return null;
      }

      const response = await $api.fetchDashboardData(year, month);

      if (response) {
        console.log('✅ API 응답 데이터:', response);

        // 1. 전사 성과 종합점수
        if (response.totalPerformance !== undefined && response.totalPerformance !== null) {
          overallPerformance.value = {
            score: response.totalPerformance.score ?? null,
            scoreChange: response.totalPerformance.scoreChange ?? null,
          };
        }

        // 2. 전체 구성원 수
        if (response.totalMembersCard !== undefined && response.totalMembersCard !== null) {
          totalMembers.value = {
            total: response.totalMembersCard.total ?? null,
            active: response.totalMembersCard.active ?? null,
          };
        }

        // 3. 고성과 예측 그룹 (S등급)
        if (
          response.highPerformanceGroupCard !== undefined &&
          response.highPerformanceGroupCard !== null
        ) {
          highPerformanceGroup.value = {
            count: response.highPerformanceGroupCard.count ?? null,
            changeCount: response.highPerformanceGroupCard.changeCount ?? null,
          };
        }

        // 4. 성과개선 집중 그룹 (D등급)
        if (response.improvementGroupCard !== undefined && response.improvementGroupCard !== null) {
          improvementGroup.value = {
            count: response.improvementGroupCard.count ?? null,
            changeCount: response.improvementGroupCard.changeCount ?? null,
          };
        }

        // 4-1. F.O.C.U.S 인덱스 그룹 점수
        if (response.indexGroup !== undefined && response.indexGroup !== null) {
          indexGroup.value = {
            fScore: response.indexGroup.fScore ?? null,
            oScore: response.indexGroup.oScore ?? null,
            cScore: response.indexGroup.cScore ?? null,
            uScore: response.indexGroup.uScore ?? null,
            sScore: response.indexGroup.sScore ?? null,
          };
        }

        // 4-2. 등급별 구성원 분포
        if (response.performGrade !== undefined && response.performGrade !== null) {
          performGrade.value = {
            sCnt: response.performGrade.sCnt ?? null,
            aCnt: response.performGrade.aCnt ?? null,
            bCnt: response.performGrade.bCnt ?? null,
            cCnt: response.performGrade.cCnt ?? null,
            dCnt: response.performGrade.dCnt ?? null,
          };
        }

        // 5. 월별 성과 추이
        if (
          response.monthlyPerformanceTrend !== undefined &&
          response.monthlyPerformanceTrend !== null
        ) {
          monthlyTrendData.value = response.monthlyPerformanceTrend;
        }

        // 6. 고성과자 예측 그룹 구성원 목록
        if (
          response.highPerformanceMemberList !== undefined &&
          response.highPerformanceMemberList !== null
        ) {
          highPerformanceMembers.value = response.highPerformanceMemberList.map((item: any) => ({
            seq: item.seq || '-',
            userId: item.userId || item.user_id || '',
            name: item.userNm || item.user_nm || '',
            departmentColor: '#00aaff' || '',
            department: item.deptNm || '',
            position: item.posNm || item.pos_nm || '',
            score: item.curPredictScore ?? item.cur_predict_score ?? null,
            scoreChange: item.diffScore ?? item.diff_score ?? null,
          }));
        }

        // 7. 성과개선 집중 그룹 구성원 목록
        if (
          response.improvementMemberList !== undefined &&
          response.improvementMemberList !== null
        ) {
          improvementMembers.value = response.improvementMemberList.map((item: any) => ({
            seq: item.seq || '-',
            userId: item.userId || item.user_id || '',
            name: item.userNm || item.user_nm || '',
            departmentColor: '#00aaff' || '',
            department: item.deptNm || '',
            position: item.posNm || item.pos_nm || '',
            score: item.curPredictScore ?? item.cur_predict_score ?? null,
            scoreChange: item.diffScore ?? item.diff_score ?? null,
          }));
        }

        // 8. 조직 성과수준 목록 (팀장 기본)
        if (
          response.managerPerformanceList !== undefined &&
          response.managerPerformanceList !== null
        ) {
          teamRankings.value = response.managerPerformanceList.map((item: any, index: number) => ({
            rank: item.seq || index + 1,
            name: item.rootDeptNm || item.root_dept_nm || '',
            leader: item.userNm || item.user_nm || '',
            score: item.avgPredictScore ?? item.avg_predict_score ?? null,
            change: item.changeScore ?? item.change_score ?? null,
          }));
        }
      } else {
        // 서버에서 데이터를 받지 못했을 때 - 이미 초기화되어 있으므로 추가 처리 불필요
        console.warn('⚠️ 서버에서 데이터를 받지 못했습니다.');
      }

      return response;
    } catch (error) {
      console.error('대시보드 데이터 조회 실패:', error);
      // 에러 발생 시에도 Mockup 데이터 유지
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 연도별 성과 추이 데이터 조회
   */
  const fetchYearlyPerformanceTrend = async (year: string, month: string) => {
    try {
      isLoading.value = true;

      // 목업 데이터 사용 모드일 때는 API 호출하지 않음
      if (useMockupData.value) {
        console.log('📊 Mockup 연도별 추이 데이터 사용 중 (API 호출 생략)');
        return null;
      }

      console.log('📊 연도별 성과 추이 API 호출:', { year, month });
      const response = await $api.fetchYearlyPerformanceTrend(year, month);

      if (response && response.yearlyPerformanceTrend) {
        console.log('✅ API 응답 데이터 사용:', response.yearlyPerformanceTrend);
        yearlyTrendData.value = response.yearlyPerformanceTrend;
      } else {
        console.warn('⚠️ 서버에서 연도별 추이 데이터를 받지 못했습니다.');
      }

      return response;
    } catch (error) {
      console.error('❌ 연도별 성과 추이 조회 실패:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 조직별 관리자 성과 목록 조회
   * @param jigGbnCd - 직급구분코드 (01:팀장, 02:부서장, 03:본부장)
   */
  const fetchManagerPerformanceList = async (year: string, month: string, jigGbnCd: string) => {
    try {
      isLoading.value = true;

      // 목업 데이터 사용 모드일 때는 API 호출하지 않음
      if (useMockupData.value) {
        console.log('📊 Mockup 조직 성과 데이터 사용 중 (API 호출 생략)');
        return null;
      }

      console.log('📊 조직별 관리자 성과 목록 API 호출:', { year, month, jigGbnCd });
      const response = await $api.fetchManagerPerformanceList(year, month, jigGbnCd);

      if (response && response.data) {
        console.log('✅ API 응답 데이터 사용:', response.data);

        // 서버 데이터를 UI 형식으로 변환
        const mappedData = response.data.map((item: any, index: number) => ({
          rank: item.seq || index + 1,
          name: item.rootDeptNm || item.root_dept_nm || '',
          leader: item.userNm || item.user_nm || '',
          score: item.avgPredictScore ?? item.avg_predict_score ?? null,
          change: item.changeScore ?? item.change_score ?? null,
        }));

        // jigGbnCd에 따라 적절한 ref에 데이터 할당
        if (jigGbnCd === '01') {
          // 팀장
          teamRankings.value = mappedData;
        } else if (jigGbnCd === '02') {
          // 부서장
          departmentRankings.value = mappedData;
        } else if (jigGbnCd === '03') {
          // 본부장
          divisionRankings.value = mappedData;
        }
      } else {
        console.warn('⚠️ 서버에서 조직 성과 목록 데이터를 받지 못했습니다.');
      }

      return response;
    } catch (error) {
      console.error('❌ 조직별 관리자 성과 목록 조회 실패:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * API 응답 데이터를 목업 형식으로 변환
   * @param apiData - API 응답 데이터 { year, userId, y5EvalScore, y4EvalScore, y3EvalScore, y2EvalScore, y1EvalScore, predictScore }
   */
  const transformApiDataToMockup = (apiData: any): MemberPerformanceHistory => {
    const currentYear = parseInt(apiData.year);
    const yearlyData = [];

    // Y5부터 Y1까지 과거 5개년도 데이터 매핑
    const yearScores = [
      { offset: 5, score: apiData.y5EvalScore },
      { offset: 4, score: apiData.y4EvalScore },
      { offset: 3, score: apiData.y3EvalScore },
      { offset: 2, score: apiData.y2EvalScore },
      { offset: 1, score: apiData.y1EvalScore },
    ];

    // 점수가 있는 년도만 추가
    yearScores.forEach(({ offset, score }) => {
      if (score !== null && score !== undefined && score !== '') {
        const year = (currentYear - offset).toString();
        const numericScore = typeof score === 'number' ? score : parseFloat(score);

        if (!isNaN(numericScore)) {
          yearlyData.push({
            year,
            score: Math.round(numericScore * 10) / 10, // 소수점 첫째자리
          });
        }
      }
    });

    // 현재 년도는 score를 null로 (예측값만 표시)
    yearlyData.push({
      year: apiData.year,
      score: null,
    });

    return {
      yearlyData,
      currentYearPrediction: apiData.predictScore
        ? Math.round(parseFloat(apiData.predictScore) * 10) / 10
        : null,
    };
  };

  /**
   * 구성원 성과 상세 조회 (모달용)
   */
  const fetchMemberPerformanceDetail = async (userId: string, year: string, month: string) => {
    try {
      isLoading.value = true;

      console.log('📊 구성원 성과 상세 조회 API 호출:', { userId, year, month });
      const response = await $api.fetchMemberPerformanceDetail(userId, year, month);

      if (response && response.data) {
        console.log('✅ API 응답 원본 데이터:', response.data);

        // API 데이터를 목업 형식으로 변환
        const transformedData = transformApiDataToMockup(response.data);
        console.log('✅ 변환된 데이터:', transformedData);

        memberPerformanceDetail.value = transformedData;
      } else {
        console.log('📦 Mockup 데이터 사용:', userId);
        // Mockup 데이터
        memberPerformanceDetail.value = getMockupMemberPerformanceDetail(userId, year);
      }

      return response;
    } catch (error) {
      console.error('❌ 구성원 성과 상세 조회 실패:', error);
      console.log('📦 에러로 인한 Mockup 데이터 사용:', userId);
      // 에러 발생 시 Mockup 데이터 사용
      memberPerformanceDetail.value = getMockupMemberPerformanceDetail(userId, year);
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Mockup 구성원 성과 상세 데이터 생성
   */
  const getMockupMemberPerformanceDetail = (
    userId: string,
    baseYear: string,
  ): MemberPerformanceHistory => {
    const currentYear = parseInt(baseYear);
    const yearlyData = [];

    // userId 기반으로 고유한 시드값 생성 (간단한 해시)
    const seed = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const baseScore = 70 + (seed % 20); // 70~90 사이의 기본 점수
    const growthRate = 0.5 + (seed % 10) / 10; // 0.5~1.5 사이의 성장률
    const currentPrediction = baseScore + 5 + (seed % 10); // 현재 예측 점수

    // 최근 5개년도 데이터 생성 (현재년도 포함)
    for (let i = 4; i >= 0; i--) {
      const year = (currentYear - i).toString();
      // 현재 년도는 null (예측값만 있음)
      let score = null;
      if (i !== 0) {
        // 과거 년도는 점진적으로 증가하는 패턴
        score = baseScore + (4 - i) * growthRate;
        // 약간의 변동성 추가
        score += ((seed * i) % 5) - 2;
        // 점수 범위 제한 (50~100)
        score = Math.max(50, Math.min(100, score));
        score = Math.round(score * 10) / 10; // 소수점 첫째자리까지
      }
      yearlyData.push({ year, score });
    }

    return {
      yearlyData,
      currentYearPrediction: Math.round(currentPrediction * 10) / 10, // 현재 년도 예측 점수
    };
  };

  /**
   * 검색 핸들러
   */
  const handleSearch = async () => {
    try {
      await fetchDashboardData(selectedYear.value, selectedMonth.value);
    } catch (error) {
      console.error('검색 실패:', error);
      alert('대시보드 데이터 조회에 실패했습니다.');
    }
  };

  /**
   * 구성원 클릭 핸들러
   */
  const handleMemberClick = async (userId: string) => {
    try {
      await fetchMemberPerformanceDetail(userId, selectedYear.value, selectedMonth.value);
    } catch (error) {
      console.error('구성원 성과 상세 조회 실패:', error);
    }
  };

  /**
   * 초기화 함수 (페이지 진입 시 호출)
   */
  const initialize = async () => {
    console.log('🚀 PaDashboardStore.initialize() 시작');

    // 현재 년도로 초기화 (월은 API 조회 후 첫 번째로 설정)
    const now = new Date();
    selectedYear.value = String(now.getFullYear());

    // 조직 성과수준 데이터 초기화 (깜박거림 방지)
    teamRankings.value = [];
    departmentRankings.value = [];
    divisionRankings.value = [];

    // 년도/월 옵션 로드
    await fetchSearchOptions();

    // 조회된 월 목록의 첫 번째 선택
    if (monthOptions.value.length > 0) {
      selectedMonth.value = monthOptions.value[0].value;
      console.log('📅 초기 월 선택:', selectedMonth.value);
    }

    // 초기 데이터 로드
    await fetchDashboardData(selectedYear.value, selectedMonth.value);

    console.log('✅ PaDashboardStore.initialize() 완료');
  };

  // 인스턴스 생성 및 저장
  storeInstance = {
    // 검색 조건
    selectedYear,
    selectedMonth,

    // 콤보박스 옵션
    yearOptions,
    monthOptions,

    // 데이터
    overallPerformance,
    totalMembers,
    highPerformanceGroup,
    improvementGroup,
    indexGroup,
    performGrade,
    yearlyTrendData,
    monthlyTrendData,
    teamRankings,
    departmentRankings,
    divisionRankings,
    highPerformanceMembers,
    improvementMembers,
    memberPerformanceDetail,

    // 로딩 상태
    isLoading,

    // 목업 데이터 사용 여부
    useMockupData,

    // API 함수
    fetchDashboardData,
    fetchYearlyPerformanceTrend,
    fetchManagerPerformanceList,
    fetchMemberPerformanceDetail,
    fetchMonthOptions,
    initialize,

    // 핸들러 함수
    handleSearch,
    handleMemberClick,
  };

  return storeInstance;
};
